/**
 * Device Store - Manages connection state and real-time data
 */

import { defineStore } from 'pinia';
import { SafetraxProtocol } from '../lib/protocol.js';

export const useDeviceStore = defineStore('device', {
  state: () => ({
    // Connection state
    connectionState: 'disconnected', // 'disconnected' | 'connecting' | 'connected' | 'error'
    lastError: null,
    protocol: null,
    firmwareVersion: null, // Firmware version string (e.g., "1.50", "1.55", "1.58")
    
    // Real-time sensor data
    realtime: {
      pitch: 0,        // degrees
      roll: 0,         // degrees
      battery: 0,      // volts
      swBattery: 0,    // volts
      timestamp: null,
      raw: null
    },

    // Source-of-truth orientation from actual device config readback
    deviceXAxisForward: false,
    
    // Streaming
    isStreaming: false,
    streamStopFn: null,
    streamInterval: 200, // ms
    
    // Battery reading
    isReadingBattery: false,
    batterySamplingInterval: null,
    batterySamplingIntervalMs: 5000, // 5 seconds

    // Wake/sleep handling
    waitingForWakeup: false,
    wakeMonitorInterval: null,
    wakeCheckInProgress: false,

    // Calibration flow UI state
    showCalibrationConfirmDialog: false,
    isCalibrationInProgress: false,
    showCalibrationCompleteDialog: false,
    
    // Console log
    consoleLog: [],
    maxLogEntries: 100
  }),

  getters: {
    isConnected: (state) => state.connectionState === 'connected',
    
    // Firmware version getters
    isV150: (state) => {
      const version = state.protocol?.firmwareVersion || state.firmwareVersion;
      return version === '1.50';
    },
    
    isV155OrLater: (state) => {
      const version = state.protocol?.firmwareVersion || state.firmwareVersion;
      if (!version) return true; // Default to v1.55 behavior if unknown
      const versionNum = parseFloat(version);
      return versionNum >= 1.55;
    },
    
    batteryStatus: (state) => {
      const v = state.realtime.battery;
      if (v === 0) return 'unknown';
      if (v < 11.5) return 'critical';
      if (v < 12.5) return 'low';
      return 'normal';
    },
    
    batteryPercent: (state) => {
      const v = state.realtime.battery;
      if (v === 0) return 0;
      // Simple linear scale: 11.5V = 0%, 14.0V = 100%
      const min = 11.5, max = 14.0;
      const percent = ((v - min) / (max - min)) * 100;
      return Math.max(0, Math.min(100, percent));
    },
    
    formattedLog: (state) => {
      return state.consoleLog.map(entry => ({
        ...entry,
        timestamp: new Date(entry.timestamp).toLocaleTimeString()
      }));
    }
  },

  actions: {
    /**
     * Connect to device
     */
    async connect() {
      this.connectionState = 'connecting';
      this.lastError = null;
      this.addLog('info', 'Requesting serial port...');
      
      try {
        // Create protocol instance if not exists
        if (!this.protocol) {
          this.protocol = new SafetraxProtocol();
          
          // Set up event listeners
          this.protocol.on('disconnect', () => {
            this.handleDisconnect();
          });
          
          this.protocol.on('error', (err) => {
            this.handleError(err);
          });
        }
        
        // Connect and login
        const connected = await this.protocol.connect();
        
        if (connected) {
          // protocol.connect() now performs a robust login handshake and only
          // returns true once login is confirmed.
          
          // Sync firmware version from protocol
          this.firmwareVersion = this.protocol.firmwareVersion;
          if (this.firmwareVersion) {
            this.addLog('info', `Firmware version: ${this.firmwareVersion}`);
          }
          
          this.connectionState = 'connected';
          this.addLog('success', 'Connected to device');
          this.addLog('success', 'Logged in successfully');

          // Probe whether device is awake. If asleep, defer all follow-up
          // commands (config read, battery read, streaming) until wake-up.
          const awake = await this.checkDeviceAwakeOnConnect();
          if (!awake) {
            this.waitingForWakeup = true;
            this.addLog('warning', 'Device appears asleep. Waiting for wake-up signal...');
            this.addLog('info', 'Press the device button or turn key on to wake it.');
            this.startWakeMonitor();
            return connected;
          }

          await this.initializeAfterWake();
        } else {
          this.connectionState = 'disconnected';
          this.addLog('warning', 'Connection cancelled by user');
        }
        
        return connected;
      } catch (err) {
        this.connectionState = 'error';
        this.lastError = err.message;
        this.addLog('error', `Connection failed: ${err.message}`);
        throw err;
      }
    },

    /**
     * Read configuration from device on connect
     */
    async readConfigOnConnect() {
      try {
        // Import config store dynamically to avoid circular dependency
        const { useConfigStore } = await import('./config.js');
        const configStore = useConfigStore();
        
        // Read config from device
        await configStore.readFromDevice();
      } catch (err) {
        // Log but don't throw - allow connection to proceed even if config read fails
        this.addLog('warning', `Could not read config on connect: ${err.message}`);
      }
    },

    /**
     * Disconnect from device
     */
    async disconnect() {
      try {
        this.stopStreaming();
        this.stopBatterySampling();
        this.stopWakeMonitor();
        this.showCalibrationConfirmDialog = false;
        this.isCalibrationInProgress = false;
        this.showCalibrationCompleteDialog = false;
        this.waitingForWakeup = false;
        this.wakeCheckInProgress = false;
        
        if (this.protocol) {
          await this.protocol.disconnect();
        }
        
        this.connectionState = 'disconnected';
        this.lastError = null;
        this.addLog('info', 'Disconnected from device');
      } catch (err) {
        this.addLog('error', `Disconnect error: ${err.message}`);
        throw err;
      }
    },

    /**
     * Handle disconnect event
     */
    handleDisconnect() {
      this.connectionState = 'disconnected';
      this.stopStreaming();
      this.stopBatterySampling();
      this.stopWakeMonitor();
      this.showCalibrationConfirmDialog = false;
      this.isCalibrationInProgress = false;
      this.showCalibrationCompleteDialog = false;
      this.waitingForWakeup = false;
      this.wakeCheckInProgress = false;
      this.deviceXAxisForward = false;
      this.addLog('warning', 'Device disconnected');
    },

    /**
     * Open calibration confirmation dialog.
     */
    requestCalibration() {
      if (!this.isConnected || this.isCalibrationInProgress) {
        return;
      }

      this.showCalibrationConfirmDialog = true;
      this.showCalibrationCompleteDialog = false;
    },

    /**
     * Cancel calibration from confirmation dialog.
     */
    cancelCalibrationRequest() {
      this.showCalibrationConfirmDialog = false;
    },

    /**
     * Start calibration command on device and enter in-progress UI state.
     */
    async startCalibration() {
      if (!this.isConnected || !this.protocol) {
        throw new Error('Device not connected');
      }

      if (this.isCalibrationInProgress) {
        return;
      }

      this.showCalibrationConfirmDialog = false;
      await this.protocol.calibrate();
      this.isCalibrationInProgress = true;
      this.showCalibrationCompleteDialog = false;
      this.addLog('info', 'Calibration in progress (15 second timer started)');
    },

    /**
     * Finish calibration timer and show completion dialog.
     */
    completeCalibrationTimer() {
      if (!this.isCalibrationInProgress) {
        return;
      }

      this.isCalibrationInProgress = false;
      this.showCalibrationCompleteDialog = true;
      this.addLog('success', 'Calibration complete');
    },

    /**
     * Dismiss completion dialog and re-enable UI.
     */
    acknowledgeCalibrationComplete() {
      this.showCalibrationCompleteDialog = false;
    },

    /**
     * Update source-of-truth orientation from device config that was read from hardware.
     * @param {boolean} xAxisForward
     */
    setDeviceXAxisForward(xAxisForward) {
      this.deviceXAxisForward = Boolean(xAxisForward);
    },

    /**
     * Detect if device is awake by requesting an IMU sample.
     * @returns {Promise<boolean>}
     */
    async checkDeviceAwakeOnConnect() {
      if (!this.protocol) {
        return false;
      }

      this.addLog('info', 'Checking if device is awake...');

      // Try twice to avoid false negatives right after reconnect.
      for (let attempt = 1; attempt <= 2; attempt++) {
        const sensorData = await this.protocol.readSensorData(1500);
        if (sensorData) {
          this.waitingForWakeup = false;
          this.updateRealtimeData(sensorData);
          this.addLog('success', 'Device is awake and responding');
          return true;
        }

        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 250));
        }
      }

      return false;
    },

    /**
     * Start all post-connect initialization that requires an awake device.
     */
    async initializeAfterWake() {
      this.waitingForWakeup = false;

      // Read config from device before starting streaming
      await this.readConfigOnConnect();

      // Read battery voltage once before starting IMU streaming
      await this.readBattery();

      // Start streaming sensor data
      this.startStreaming();

      // Start periodic battery sampling (every 5 seconds)
      this.startBatterySampling();
    },

    /**
     * Poll device while asleep and continue initialization once it wakes.
     */
    startWakeMonitor() {
      if (this.wakeMonitorInterval) {
        return;
      }

      this.wakeMonitorInterval = setInterval(async () => {
        if (!this.isConnected || !this.waitingForWakeup || !this.protocol) {
          return;
        }

        if (this.wakeCheckInProgress) {
          return;
        }

        this.wakeCheckInProgress = true;
        try {
          const sensorData = await this.protocol.readSensorData(1200);
          if (!sensorData) {
            return;
          }

          this.updateRealtimeData(sensorData);
          this.waitingForWakeup = false;
          this.stopWakeMonitor();
          this.addLog('success', 'Device woke up, continuing initialization...');
          await this.initializeAfterWake();
        } catch (err) {
          console.error('Wake monitor probe failed:', err);
        } finally {
          this.wakeCheckInProgress = false;
        }
      }, 2000);
    },

    /**
     * Stop wake-up monitor polling.
     */
    stopWakeMonitor() {
      if (this.wakeMonitorInterval) {
        clearInterval(this.wakeMonitorInterval);
        this.wakeMonitorInterval = null;
      }
    },

    /**
     * Handle error event
     */
    handleError(error) {
      this.lastError = error.message;
      this.addLog('error', error.message);
    },

    /**
     * Start streaming real-time sensor data
     */
    startStreaming() {
      if (this.isStreaming || !this.protocol) {
        return;
      }
      
      this.isStreaming = true;
      this.addLog('info', `Started streaming (${this.streamInterval}ms interval)`);
      
      this.streamStopFn = this.protocol.startStreaming(
        (data) => {
          this.updateRealtimeData(data);
        },
        this.streamInterval
      );
    },

    /**
     * Stop streaming sensor data
     */
    stopStreaming() {
      if (!this.isStreaming) {
        return;
      }
      
      if (this.streamStopFn) {
        this.streamStopFn();
        this.streamStopFn = null;
      }
      
      this.isStreaming = false;
      this.addLog('info', 'Stopped streaming');
    },

    /**
     * Update real-time sensor data
     */
    updateRealtimeData(data) {
      if (data) {
        // Use orientation sourced from actual device config read/readback only.
        const adjustedData = this.deviceXAxisForward
          ? {
              ...data,
              pitch: data.roll,
              roll: data.pitch
            }
          : data;

        // Preserve battery values if the incoming data has them as 0
        // (IMU data doesn't include battery, only T command does)
        const battery = adjustedData.battery === 0 ? this.realtime.battery : adjustedData.battery;
        const swBattery = adjustedData.swBattery === 0 ? this.realtime.swBattery : adjustedData.swBattery;
        
        this.realtime = {
          ...adjustedData,
          battery,
          swBattery,
          timestamp: new Date()
        };
      }
    },

    /**
     * Add log entry
     */
    addLog(type, message) {
      const entry = {
        type,     // 'info' | 'success' | 'warning' | 'error'
        message,
        timestamp: new Date()
      };
      
      this.consoleLog.unshift(entry);
      
      // Keep only last N entries
      if (this.consoleLog.length > this.maxLogEntries) {
        this.consoleLog = this.consoleLog.slice(0, this.maxLogEntries);
      }
      
      // Also log to browser console
      const logFn = {
        info: console.log,
        success: console.log,
        warning: console.warn,
        error: console.error
      }[type] || console.log;
      
      logFn(`[${type.toUpperCase()}] ${message}`);
    },

    /**
     * Clear console log
     */
    clearLog() {
      this.consoleLog = [];
      this.addLog('info', 'Log cleared');
    },
    
    /**
     * Read battery voltage manually
     */
    async readBattery() {
      if (!this.protocol || !this.isConnected) {
        throw new Error('Not connected to device');
      }

      if (this.waitingForWakeup) {
        throw new Error('Device is asleep. Wake device before reading battery.');
      }
      
      if (this.isReadingBattery) {
        console.warn('Battery read already in progress');
        return;
      }
      
      this.isReadingBattery = true;
      this.addLog('info', 'Reading battery voltage...');
      
      try {
        const batteryData = await this.protocol.readBatteryManual();
        
        // Update realtime data with battery values
        this.realtime = {
          ...this.realtime,
          battery: batteryData.battery,
          swBattery: batteryData.swBattery,
          timestamp: new Date()
        };
        
        this.addLog('success', `Battery: ${batteryData.battery.toFixed(1)}V`);
      } catch (err) {
        this.addLog('error', `Battery read failed: ${err.message}`);
        throw err;
      } finally {
        this.isReadingBattery = false;
      }
    },
    
    /**
     * Start periodic battery sampling
     */
    startBatterySampling() {
      if (this.batterySamplingInterval) {
        console.warn('Battery sampling already started');
        return;
      }
      
      this.addLog('info', `Started battery sampling (${this.batterySamplingIntervalMs / 1000}s interval)`);
      
      // Set up interval to read battery every 5 seconds
      this.batterySamplingInterval = setInterval(async () => {
        // Only sample if connected and not manually reading
        if (this.isConnected && !this.isReadingBattery) {
          try {
            await this.readBatterySilent();
          } catch (err) {
            console.error('Battery sampling failed:', err);
            // Don't spam the log with errors - just log to console
          }
        }
      }, this.batterySamplingIntervalMs);
    },
    
    /**
     * Stop periodic battery sampling
     */
    stopBatterySampling() {
      if (this.batterySamplingInterval) {
        clearInterval(this.batterySamplingInterval);
        this.batterySamplingInterval = null;
        this.addLog('info', 'Stopped battery sampling');
      }
    },
    
    /**
     * Read battery voltage silently (without logging)
     */
    async readBatterySilent() {
      if (!this.protocol || !this.isConnected) {
        return;
      }

      if (this.waitingForWakeup) {
        return;
      }
      
      // Don't interrupt critical operations (like config writes)
      if (this.protocol.isCriticalOperation) {
        console.log('[Battery Silent] Skipping - critical operation in progress');
        return;
      }
      
      if (this.isReadingBattery) {
        return;
      }
      
      this.isReadingBattery = true;
      
      try {
        const batteryData = await this.protocol.readBatteryManual();
        
        // Update realtime data with battery values
        this.realtime = {
          ...this.realtime,
          battery: batteryData.battery,
          swBattery: batteryData.swBattery,
          timestamp: new Date()
        };
      } catch (err) {
        // Silent failure - just log to console
        console.error('Battery sample failed:', err.message);
      } finally {
        this.isReadingBattery = false;
      }
    }
  }
});
